import { KnownProjectDtoProjectType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { DialogContent, useMediaQuery, useTheme } from '@mui/material';
import { createContext, useContext, useMemo, useState } from 'react';
import {
  useQueryGetProjectOptional,
  useQueryGetProjectSummaryOptional,
  useQueryListProjects
} from '../../core/queries/projects.queries';
import { convertHyphenated } from '../../core/util/string-conversion';
// import useLocalStorage from '../../hooks/use-local-storage.hook';
import { Modal } from '../../ui/components/pure/modal/modal.component';
import {
  ProjectSummaryContent,
  ProjectSummaryContentProps
} from '../../ui/components/pure/project-summary/project-summary-content.component';
import { ProjectSummaryDrawer } from '../../ui/components/pure/project-summary/project-summary-drawer.component';

export interface ProjectSummaryContextValue {
  isOpen: boolean;
  onToggle: (open: boolean) => void;
}

// TO DO: Keep this for when we update the Project Summary Drawer to be "persistent"
// const PROJECT_SUMMARY_LOCAL_STORAGE_KEY = 'project-summary-drawer-state';

export const ProjectSummaryContext = createContext<ProjectSummaryContextValue>({} as ProjectSummaryContextValue);

export const useProjectSummaryContext = () => {
  const { isOpen, onToggle } = useContext(ProjectSummaryContext);

  return { isOpen, onToggle };
};

export function ProjectSummaryContextProvider({ children }: { children: React.ReactNode }) {
  // TO DO: When we update the Project Summary Drawer to be "persistent" we can use this hook
  // to store the state of the drawer in local storage
  // const [openState, setIsOpen] = useLocalStorage(PROJECT_SUMMARY_LOCAL_STORAGE_KEY, { isOpen: false });
  const [openState, setIsOpen] = useState({ isOpen: false });

  return (
    <ProjectSummaryContext.Provider
      value={{
        isOpen: openState.isOpen,
        onToggle: (isOpen: boolean) =>
          setIsOpen(() => ({
            isOpen
          }))
      }}
    >
      {children}
    </ProjectSummaryContext.Provider>
  );
}

export function ProjectSummaryDrawerContainer(props: { params?: { projectID?: string; shortcode?: string } }) {
  const { isOpen, onToggle } = useProjectSummaryContext();

  const projectSummaryResult = useQueryGetProjectSummaryOptional(props.params?.projectID);
  const projectResult = useQueryGetProjectOptional(props.params?.projectID);

  // Only ask for the parent when the caller can read it. A carrier or region project sits beneath
  // one its admins have no access to, so asking outright 403s on every page, for a title prefix.
  const accessibleProjects = useQueryListProjects();
  const parentID = projectResult.data?.parentID;
  const canReadParent = !!parentID && (accessibleProjects.data ?? []).some((project) => project.id === parentID);
  const parentProjectResult = useQueryGetProjectOptional(canReadParent ? parentID : undefined);

  const projectTitle = useMemo(() => {
    let title: string = '';
    if (projectResult.data) {
      title = projectResult.data.name;
    }
    // We include the parent project name in the title if the parent project is not of type Project
    if (
      title &&
      parentProjectResult.data &&
      parentProjectResult.data.projectType !== KnownProjectDtoProjectType.Project
    ) {
      title = `${parentProjectResult.data.name} > ${title}`;
    }
    return title;
  }, [parentProjectResult.data, projectResult.data]);

  const data = useMemo((): ProjectSummaryContentProps['data'] => {
    return {
      title: projectTitle,
      projectType: projectResult.data?.projectType
        ? convertHyphenated(projectResult.data.projectType, { capitalizeEachWord: true })
        : undefined,
      address: projectResult.data?.address,
      summary: projectSummaryResult.data
    };
  }, [projectResult.data?.address, projectResult.data?.projectType, projectSummaryResult.data, projectTitle]);

  const isLoading = useMemo(
    () => projectSummaryResult.isLoading || projectResult.isLoading || parentProjectResult.isLoading,
    [parentProjectResult.isLoading, projectResult.isLoading, projectSummaryResult.isLoading]
  );

  const theme = useTheme();
  const isSx = useMediaQuery(theme.breakpoints.down('sm'));

  if (isSx) {
    return (
      <Modal title="Project Summary" open={isOpen} onClose={() => onToggle(false)}>
        <DialogContent>
          <ProjectSummaryContent data={data} isLoading={isLoading} />
        </DialogContent>
      </Modal>
    );
  }

  return <ProjectSummaryDrawer data={data} isOpen={isOpen} onToggle={onToggle} isLoading={isLoading} />;
}
